import { 
  Ruler, User, Eye, Brush, Cigarette, Wine, Baby, GraduationCap, 
  Briefcase, Heart, Cat, Smile 
} from "lucide-react";

// Labels pour les attributs avancés
const ATTRIBUTE_LABELS = {
  height: {
    label: { fr: "Taille", en: "Height" },
    icon: Ruler,
    values: {
      "<150": "< 150 cm",
      "150-159": "150-159 cm",
      "160-169": "160-169 cm",
      "170-179": "170-179 cm",
      "180-189": "180-189 cm",
      "190+": "190+ cm"
    }
  },
  body_type: {
    label: { fr: "Silhouette", en: "Body type" },
    icon: User,
    values: {
      slim: { fr: "Mince", en: "Slim" },
      athletic: { fr: "Athlétique", en: "Athletic" },
      average: { fr: "Normal(e)", en: "Average" },
      curvy: { fr: "Ronde", en: "Curvy" },
      full: { fr: "Corpulent(e)", en: "Full" }
    }
  },
  eyes: {
    label: { fr: "Yeux", en: "Eyes" },
    icon: Eye,
    values: {
      brown: { fr: "Marron", en: "Brown" },
      blue: { fr: "Bleu", en: "Blue" },
      green: { fr: "Vert", en: "Green" },
      hazel: { fr: "Noisette", en: "Hazel" },
      other: { fr: "Autre", en: "Other" }
    }
  },
  hair: {
    label: { fr: "Cheveux", en: "Hair" },
    icon: Brush,
    values: {
      brown: { fr: "Bruns", en: "Brown" },
      blonde: { fr: "Blonds", en: "Blonde" },
      black: { fr: "Noirs", en: "Black" },
      red: { fr: "Roux", en: "Red" },
      gray: { fr: "Gris", en: "Gray" },
      bald: { fr: "Chauve", en: "Bald" }
    }
  },
  smoking: {
    label: { fr: "Fumeur", en: "Smoker" },
    icon: Cigarette,
    values: {
      no: { fr: "Non", en: "No" },
      occasional: { fr: "Occasionnel", en: "Occasional" },
      yes: { fr: "Oui", en: "Yes" }
    }
  },
  drinking: {
    label: { fr: "Alcool", en: "Alcohol" },
    icon: Wine,
    values: {
      never: { fr: "Jamais", en: "Never" },
      occasional: { fr: "Occasionnel", en: "Occasional" },
      regular: { fr: "Régulier", en: "Regular" }
    }
  },
  children: {
    label: { fr: "Enfants", en: "Children" },
    icon: Baby,
    values: {
      none: { fr: "Aucun", en: "None" },
      has: { fr: "Oui, avec moi", en: "Yes, with me" },
      shared: { fr: "Garde partagée", en: "Shared custody" }
    }
  },
  wants_children: {
    label: { fr: "Souhaite enfants", en: "Wants children" },
    icon: Baby,
    values: {
      yes: { fr: "Oui", en: "Yes" },
      no: { fr: "Non", en: "No" },
      maybe: { fr: "Peut-être", en: "Maybe" }
    }
  },
  education: {
    label: { fr: "Études", en: "Education" },
    icon: GraduationCap,
    values: {
      high_school: { fr: "Bac", en: "High school" },
      bac2: { fr: "Bac +2", en: "Associate" },
      bachelor: { fr: "Bac +3", en: "Bachelor" },
      master: { fr: "Bac +5", en: "Master" },
      phd: { fr: "Doctorat", en: "PhD" }
    }
  },
  profession: {
    label: { fr: "Profession", en: "Profession" },
    icon: Briefcase,
    values: {
      student: { fr: "Étudiant(e)", en: "Student" },
      employee: { fr: "Employé(e)", en: "Employee" },
      manager: { fr: "Cadre", en: "Manager" },
      freelance: { fr: "Indépendant", en: "Freelance" },
      entrepreneur: { fr: "Entrepreneur", en: "Entrepreneur" },
      retired: { fr: "Retraité(e)", en: "Retired" }
    }
  },
  relationship_type: {
    label: { fr: "Recherche", en: "Looking for" },
    icon: Heart,
    values: {
      serious: { fr: "Relation sérieuse", en: "Serious" },
      casual: { fr: "Occasionnel", en: "Casual" },
      friendship: { fr: "Amitié", en: "Friendship" },
      undecided: { fr: "Je ne sais pas", en: "Not sure" }
    }
  },
  pets: {
    label: { fr: "Animaux", en: "Pets" },
    icon: Cat,
    values: {
      none: { fr: "Aucun", en: "None" },
      dog: { fr: "Chien", en: "Dog" },
      cat: { fr: "Chat", en: "Cat" },
      other: { fr: "Autre", en: "Other" },
      loves: { fr: "Aime les animaux", en: "Loves animals" }
    }
  },
  personality: {
    label: { fr: "Personnalité", en: "Personality" },
    icon: Smile,
    values: {
      calm: { fr: "Calme", en: "Calm" },
      dynamic: { fr: "Dynamique", en: "Dynamic" },
      funny: { fr: "Drôle", en: "Funny" },
      introvert: { fr: "Introverti(e)", en: "Introvert" },
      extrovert: { fr: "Extraverti(e)", en: "Extrovert" }
    }
  }
};

export function getAttributeLabel(key, value, language = 'fr') {
  const config = ATTRIBUTE_LABELS[key];
  if (!config || !value) return null;
  
  const valueConfig = config.values[value];
  if (!valueConfig) return value;
  
  return typeof valueConfig === 'string' ? valueConfig : valueConfig[language];
}

export default function ProfileAttributes({ user, language = 'fr', compact = false }) {
  // Filtrer les attributs qui ont une valeur
  const attributes = Object.entries(ATTRIBUTE_LABELS)
    .filter(([key]) => user[key] && user[key] !== '')
    .map(([key, config]) => ({
      key,
      label: config.label[language],
      value: getAttributeLabel(key, user[key], language),
      Icon: config.icon
    }))
    .filter(attr => attr.value);

  if (attributes.length === 0) return null;

  if (compact) {
    // Version compacte pour les cartes de profil
    return (
      <div className="flex flex-wrap gap-1.5">
        {attributes.slice(0, 6).map(({ key, value, Icon }) => (
          <div 
            key={key}
            className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded-full text-xs text-white/80"
          >
            <Icon className="w-3 h-3" />
            <span>{value}</span>
          </div>
        ))}
        {attributes.length > 6 && (
          <div className="px-2 py-1 bg-white/10 rounded-full text-xs text-white/60">
            +{attributes.length - 6}
          </div>
        )}
      </div>
    );
  }

  // Version complète pour la page de profil détaillé
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-white mb-3">
        {language === 'fr' ? 'Informations' : 'Details'}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {attributes.map(({ key, label, value, Icon }) => (
          <div 
            key={key}
            className="flex items-center gap-3 p-3 bg-card/50 rounded-xl border border-white/5"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center">
              <Icon className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground truncate">{label}</p>
              <p className="text-sm text-white font-medium truncate">{value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
